import React from 'react';
import DisclaimerText from './DisclaimerText';

const ShortTermHelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-green-600 to-blue-600 px-6 py-4 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-white">📖 How to Use: Short-Term Investment Model</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6">
            {/* Introduction */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">🎯 Overview</h3>
              <p className="text-gray-700">
                The Short-Term Investment Model helps you identify stocks with strong momentum and technical indicators 
                suitable for short-term trading (1-3 months holding period). The model analyzes stocks based on multiple 
                technical criteria including trend strength, momentum, volume, and relative performance.
              </p>
            </section>

            {/* How to Configure Parameters */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">⚙️ Configure Analysis Parameters</h3>
              
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-700">📊 Stock Index</h4>
                  <p className="text-gray-600 text-sm">
                    Select which stock universe to scan (NIFTY 50, NIFTY BANK, NIFTY IT). 
                    You can also upload a custom CSV file with your own list of stocks.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">📈 ADX Threshold (Default: 20)</h4>
                  <p className="text-gray-600 text-sm">
                    ADX (Average Directional Index) measures trend strength. Higher values indicate stronger trends.
                    <br/>• <strong>20-25:</strong> Moderate trend strength
                    <br/>• <strong>25-50:</strong> Strong trend (recommended for short-term)
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">📊 RSI Range (Default: 50-70)</h4>
                  <p className="text-gray-600 text-sm">
                    RSI (Relative Strength Index) measures momentum.
                    <br/>• <strong>50-70:</strong> Bullish momentum without being overbought
                    <br/>• <strong>Above 70:</strong> Overbought territory (risky)
                    <br/>• <strong>Below 30:</strong> Oversold (may bounce but risky)
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">📊 Volume Ratio (Default: 1.2)</h4>
                  <p className="text-gray-600 text-sm">
                    Current volume vs 20-day average volume. Higher values indicate increased interest.
                    <br/>• <strong>1.2-1.5:</strong> Above average volume
                    <br/>• <strong>Above 1.5:</strong> High volume surge
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">📊 RS Threshold (Default: 1.0)</h4>
                  <p className="text-gray-600 text-sm">
                    Relative Strength vs NIFTY 50. Measures outperformance.
                    <br/>• <strong>Above 1.0:</strong> Outperforming the market
                    <br/>• <strong>Below 1.0:</strong> Underperforming the market
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">✅ Above All MAs Filter</h4>
                  <p className="text-gray-600 text-sm">
                    When enabled, only shows stocks trading above all moving averages (20/50/100/200 day). 
                    This is a strong bullish signal indicating uptrend across all timeframes.
                  </p>
                </div>
              </div>
            </section>

            {/* How to Upload CSV */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">📤 Upload Custom Stock List (CSV)</h3>
              <p className="text-gray-700 mb-2">
                You can analyze your own custom list of stocks by uploading a CSV file:
              </p>
              <ol className="list-decimal list-inside text-gray-600 space-y-1 ml-4">
                <li>Create a CSV file with a column named <strong>"Symbol"</strong></li>
                <li>Add stock symbols in the format: <code className="bg-gray-100 px-2 py-1 rounded">RELIANCE.NSE</code>, <code className="bg-gray-100 px-2 py-1 rounded">TCS.NSE</code></li>
                <li>Click the "Upload CSV File" button and select your file</li>
                <li>The model will analyze your custom stock list</li>
              </ol>
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Example CSV format:</strong>
                  <br/>
                  <code className="text-xs bg-white px-2 py-1 rounded mt-1 inline-block">
                    Symbol<br/>
                    RELIANCE.NSE<br/>
                    TCS.NSE<br/>
                    INFY.NSE
                  </code>
                </p>
              </div>
            </section>

            {/* How to Interpret Results */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">📊 Understanding the Results</h3>
              <p className="text-gray-700 mb-2">
                Stocks that pass all your filter criteria will be displayed in the results table:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                <li><strong>Symbol:</strong> Stock ticker symbol</li>
                <li><strong>Close:</strong> Latest closing price</li>
                <li><strong>% Change:</strong> Daily price change percentage</li>
                <li><strong>ADX:</strong> Trend strength indicator</li>
                <li><strong>RSI:</strong> Momentum indicator (50-70 is ideal)</li>
                <li><strong>MA20/MA50:</strong> Moving averages (support/resistance levels)</li>
                <li><strong>Volume:</strong> Trading volume</li>
                <li><strong>Vol Ratio:</strong> Volume vs 20-day average</li>
                <li><strong>RS-55:</strong> Relative strength vs NIFTY 50 (above 1.0 is outperforming)</li>
              </ul>
            </section>

            {/* Trading Action */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">🎯 Taking Action</h3>
              <p className="text-gray-700 mb-2">
                For each stock in the results:
              </p>
              <ol className="list-decimal list-inside text-gray-600 space-y-1 ml-4">
                <li>Click the <strong>"Trade"</strong> button to open the Order Ticket modal</li>
                <li>Configure your trade parameters (quantity, prices, stop loss)</li>
                <li>The order will be placed through your connected broker (if configured)</li>
                <li>You can also <strong>download the results</strong> as an Excel file for further analysis</li>
              </ol>
            </section>

            {/* Tips */}
            <section className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">💡 Pro Tips</h3>
              <ul className="list-disc list-inside text-yellow-800 space-y-1 text-sm ml-4">
                <li>Start with stricter filters (higher ADX, narrower RSI range) to get quality stocks</li>
                <li>Enable "Above All MAs" for strongest bullish signals</li>
                <li>Higher Volume Ratio indicates institutional interest or major news</li>
                <li>Use the RS-55 filter to find stocks outperforming the broader market</li>
                <li>Download results to track stocks over time and build your watchlist</li>
              </ul>
            </section>

            {/* Platform Disclaimer */}
            <DisclaimerText />
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t">
            <button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
            >
              Got it! Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShortTermHelpModal;

